import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../config';

// Tre fasce di priorità (stessi colori/nomi della pagina Priorità — niente viola, mai):
// P1 verde = appena passati a fibra, P2 blu = mai lavorati, P3 arancione = timing scaduto.
const FASCE = [
  { k: 'ftth', l: 'P1', full: 'Nuova fibra', c: 'var(--ok)' },
  { k: 'nuovo', l: 'P2', full: 'Nuovi', c: 'var(--info)' },
  { k: 'scaduto', l: 'P3', full: 'Da ricontattare', c: '#e08a1e' },
];

/* Compositore export: parte da una SELEZIONE (passata dalla Dashboard o da una selezione salvata
   di lavoro) + filtri + quantità + raggruppamento, conteggio pronti live, creazione lotto.
   La scelta delle aree si fa nella Dashboard o caricando una selezione salvata (niente picker qui). */
export default function ExportComposer({ initial, onExported }) {
  // selezione aree
  const [selRegioni, setSelRegioni] = useState(new Set(initial?.regioni || []));
  const [selProvince, setSelProvince] = useState(new Set(initial?.province || []));
  const [comune, setComune] = useState(initial?.comune || '');
  // priorità (fasce): quando almeno una è spuntata, l'esito non si applica più
  const [selPriorita, setSelPriorita] = useState(new Set(initial?.priorita || []));
  // filtri
  const [esito, setEsito] = useState('NON TIM'); // NON TIM | TIM | TUTTI
  const [tipoImpresa, setTipoImpresa] = useState('');
  const [consenso, setConsenso] = useState('');
  const [quantita, setQuantita] = useState('');
  const [dims, setDims] = useState([]); // raggruppamento: esito/regione/provincia/tipologia
  const [nota, setNota] = useState('');
  // conteggio live
  const [available, setAvailable] = useState(null);
  const [loadingCount, setLoadingCount] = useState(false);
  // aree salvate
  const [gruppi, setGruppi] = useState([]);
  const [nomeGruppo, setNomeGruppo] = useState('');
  // azioni
  const [creating, setCreating] = useState(false);

  const hasPriorita = selPriorita.size > 0;
  const togglePriorita = (k) => setSelPriorita(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const toggleDim = (d) => setDims(p => p.includes(d) ? p.filter(x => x !== d) : [...p, d]);

  const loadGruppi = useCallback(() => {
    axios.get(`${API}/export/gruppi`).then(r => setGruppi(r.data?.items || [])).catch(() => {});
  }, []);
  useEffect(() => { loadGruppi(); }, [loadGruppi]);

  // ── conteggio pronti live (server, debounced) ──
  useEffect(() => {
    const t = setTimeout(() => {
      setLoadingCount(true);
      axios.post(`${API}/export/count`, {
        regioni: [...selRegioni], province: [...selProvince],
        comune: comune || undefined,
        tipo_impresa: tipoImpresa || undefined,
        consenso: consenso || undefined,
        risultato: esito,
        priorita: hasPriorita ? [...selPriorita] : undefined,
      }).then(r => setAvailable(r.data?.count ?? 0))
        .catch(() => setAvailable(null))
        .finally(() => setLoadingCount(false));
    }, 350);
    return () => clearTimeout(t);
  }, [selRegioni, selProvince, comune, tipoImpresa, consenso, esito, selPriorita, hasPriorita]);

  // ── selezione ──
  const toggleArea = (nome, level) => {
    if (level === 'regione') {
      setSelRegioni(prev => { const n = new Set(prev); n.has(nome) ? n.delete(nome) : n.add(nome); return n; });
    } else {
      setSelProvince(prev => { const n = new Set(prev); n.has(nome) ? n.delete(nome) : n.add(nome); return n; });
    }
  };
  const clearSel = () => { setSelRegioni(new Set()); setSelProvince(new Set()); setComune(''); };
  const nAree = selRegioni.size + selProvince.size + (comune ? 1 : 0);

  // ── gruppi ──
  const salvaGruppo = async () => {
    if (nAree === 0) { toast.error('Seleziona almeno un\'area'); return; }
    if (!nomeGruppo.trim()) { toast.error('Dai un nome alla selezione'); return; }
    try {
      await axios.post(`${API}/export/gruppi`, { nome: nomeGruppo.trim(), regioni: [...selRegioni], province: [...selProvince] });
      toast.success('Aree salvate'); setNomeGruppo(''); loadGruppi();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio'); }
  };
  const caricaGruppo = (g) => { setSelRegioni(new Set(g.regioni || [])); setSelProvince(new Set(g.province || [])); };
  const eliminaGruppo = async (id, e) => { e.stopPropagation(); try { await axios.delete(`${API}/export/gruppi/${id}`); loadGruppi(); } catch { toast.error('Errore'); } };

  // ── export ──
  const esporta = async (quantitaOverride) => {
    // nessuna area = tutti i disponibili (filtri tipo/consenso comunque applicati)
    if (!nota.trim()) { toast.error('La nota è obbligatoria'); return; }
    if (!available) { toast.error('Nessun numero disponibile con questi criteri'); return; }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/export/genera`, {
        filtri: { tipo_impresa: tipoImpresa || undefined, consenso: consenso || undefined, comune: comune || undefined },
        regioni: [...selRegioni], province: [...selProvince],
        raggruppa: dims,
        risultato: esito,
        quantita: quantitaOverride != null ? quantitaOverride : (quantita ? Number(quantita) : 0),
        nota: nota.trim(),
        priorita: hasPriorita ? [...selPriorita] : undefined,
      });
      const lotto = r.data?.lotto;
      toast.success(
        `Lotto #${lotto?.numero_lotto} creato (${lotto?.totale_numeri} numeri) — `
        + 'i numeri escono dal pool solo al PRIMO SCARICAMENTO dallo Storico: fino ad allora restano lavorabili.',
        { duration: 8000 },
      );
      setNota('');
      onExported?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore export');
    }
    setCreating(false);
  };

  const lbl = { fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4, display: 'block' };
  const inp = { width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 7, fontSize: 13, background: 'var(--surface)' };

  return (
    <div className="xc">
      {/* 1. AREE — selezione dalla Dashboard o da una selezione salvata */}
      <div className="xc-block">
        <div className="xc-step">1 · Aree (dalla Dashboard o da una selezione salvata)</div>
        <div className="xc-chips">
          {nAree === 0 && (
            <span className="xc-empty">
              Nessuna area selezionata = esporti <b>tutti i disponibili</b> (tutta Italia).
              Scegli le aree dalla <b>Dashboard</b> ("Manda all'Export") oppure carica una
              <b> selezione salvata</b> qui sotto.
            </span>
          )}
          {[...selRegioni].map(k => (
            <span key={`r-${k}`} className="xc-chip">{k}<button onClick={() => toggleArea(k, 'regione')}>✕</button></span>
          ))}
          {[...selProvince].map(k => (
            <span key={`p-${k}`} className="xc-chip">{k}<button onClick={() => toggleArea(k, 'provincia')}>✕</button></span>
          ))}
          {comune && (
            <span key="comune" className="xc-chip">Comune: {comune}<button onClick={() => setComune('')}>✕</button></span>
          )}
          {nAree > 0 && <button className="xc-clear" onClick={clearSel}>svuota</button>}
        </div>
      </div>

      {/* priorità: sopra il filtro esito, che quando attive smette di applicarsi */}
      <div className="xc-block">
        <span style={lbl}>Priorità</span>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
          {FASCE.map(f => {
            const on = selPriorita.has(f.k);
            return (
              <label key={f.k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: on ? f.c : 'var(--text-secondary)', cursor: 'pointer' }}>
                <input type="checkbox" checked={on} onChange={() => togglePriorita(f.k)} style={{ accentColor: f.c, width: 15, height: 15, cursor: 'pointer' }} />
                {f.l} · {f.full}
              </label>
            );
          })}
        </div>
      </div>

      {/* 2-3-4 filtri */}
      <div className="xc-block xc-filters">
        <div>
          <span style={lbl}>Esito</span>
          <select style={{ ...inp, opacity: hasPriorita ? 0.5 : 1 }} value={esito} onChange={e => setEsito(e.target.value)} disabled={hasPriorita}>
            <option value="NON TIM">NON TIM</option>
            <option value="TIM">TIM</option>
            <option value="TUTTI">Tutti (TIM + NON TIM)</option>
          </select>
          {hasPriorita && (
            <span style={{ display: 'block', fontSize: 11, fontStyle: 'italic', color: 'var(--text-secondary)', marginTop: 4 }}>
              Con le priorità attive l'esito non si applica: P1 include anche i clienti TIM passati a fibra.
            </span>
          )}
        </div>
        <div>
          <span style={lbl}>Tipo impresa</span>
          <select style={inp} value={tipoImpresa} onChange={e => setTipoImpresa(e.target.value)}>
            <option value="">Tutti</option><option value="azienda">Azienda</option><option value="privato">Privato</option>
          </select>
        </div>
        <div>
          <span style={lbl}>Consenso</span>
          <select style={inp} value={consenso} onChange={e => setConsenso(e.target.value)}>
            <option value="">Tutti</option><option value="si">Sì</option><option value="no">No</option>
          </select>
        </div>
        <div>
          <span style={lbl}>Quantità</span>
          <input type="number" min={1} style={inp} placeholder="Tutti" value={quantita} onChange={e => setQuantita(e.target.value)} />
        </div>
        <div>
          <span style={lbl}>Suddividi file</span>
          <div className="xc-dims">
            {[{ d: 'esito', l: 'Esito' }, { d: 'regione', l: 'Regione' }, { d: 'provincia', l: 'Provincia' }, { d: 'tipologia', l: 'Tipologia' }].map(({ d, l }) => (
              <button key={d} type="button" className={`xc-dim${dims.includes(d) ? ' on' : ''}`} onClick={() => toggleDim(d)}>{l}</button>
            ))}
          </div>
        </div>
      </div>

      {/* riepilogo live */}
      <div className="xc-summary">
        <span>Pronti all'export</span>
        <b>{loadingCount ? '…' : (available != null ? available.toLocaleString('it-IT') : '—')}</b>
      </div>

      {/* nota + azioni */}
      <div className="xc-block">
        <span style={lbl}>Nota lotto <span style={{ color: '#ef4444' }}>*</span></span>
        <input style={inp} placeholder="Descrizione obbligatoria del lotto…" value={nota} onChange={e => setNota(e.target.value)} />
        <div className="xc-actions">
          <button className="btn btn-success" disabled={creating || !available} onClick={() => esporta()}>
            {creating ? 'Creazione…' : 'Crea lotto'}
          </button>
          <button className="btn btn-primary" disabled={creating || !available} onClick={() => esporta(1000)}>
            Crea lotto (prossimi 1000)
          </button>
        </div>
      </div>

      {/* aree salvate */}
      <div className="xc-block xc-groups-block">
        <span style={lbl}>Aree salvate</span>
        <div className="xc-row">
          <input style={inp} placeholder="Nome (per i team)" value={nomeGruppo} onChange={e => setNomeGruppo(e.target.value)} />
          <button className="btn btn-secondary" style={{ whiteSpace: 'nowrap' }} onClick={salvaGruppo}>Salva selezione</button>
        </div>
        <div className="xc-chips" style={{ marginTop: 8 }}>
          {gruppi.length === 0 && <span className="xc-empty">Nessuna area salvata.</span>}
          {gruppi.map(g => (
            <span key={g.id} className="xc-chip xc-chip-g" title="Carica selezione" onClick={() => caricaGruppo(g)}>
              {g.nome}<small>{(g.regioni?.length || 0)}R/{(g.province?.length || 0)}P</small>
              <button onClick={(e) => eliminaGruppo(g.id, e)}>✕</button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
